import {
    Autocomplete,
    Box,
    Button,
    IconButton,
    TextField,
    Typography
} from "@mui/material";
import {
    Dispatch,
    SetStateAction,
    useMemo,
    useState
} from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Contact } from "../../models";

type AssessmentScheduleAuthorizedUserFormProps = {
    contacts: Array<Contact>;
    setIsOpenCreateContactDialog: Dispatch<SetStateAction<boolean>>;
    authorizedIdentifiers: Array<string>;
    setAuthorizedIdentifiers: Dispatch<SetStateAction<Array<string>>>;
};

export const AssessmentScheduleAuthorizedIdentifiersForm = (props: AssessmentScheduleAuthorizedUserFormProps) => {
    const {
        contacts,
        setIsOpenCreateContactDialog,
        authorizedIdentifiers,
        setAuthorizedIdentifiers
    } = props;
    const [searchValue, setSearchValue] = useState<string>("");
    const sortedContactOptions = useMemo(() => {
        return contacts.filter(contact => !authorizedIdentifiers.includes(contact.id))
            .sort((a, b) => {
                if (a.name && b.name) {
                    return a.name.localeCompare(b.name);
                }
                return 0;
            });
    }, [contacts, authorizedIdentifiers]);

    return (
        <Box display="flex" flexDirection="column" gap={0.5}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Configure the users authorized to perform assessments.
            </Typography>
            <Box display="flex" flexDirection="column" gap={0.5}>
                <Box display="flex" gap={1} alignItems="center" justifyContent="space-between">
                    <Typography fontWeight={500}>
                        Authorized Users:
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={() => setIsOpenCreateContactDialog(true)}
                        size="small"
                        sx={{ textTransform: "none", ml: "auto" }}
                        color="inherit"
                    >
                        Create New Contact
                    </Button>
                </Box>
                <Autocomplete
                    size="small"
                    fullWidth
                    options={sortedContactOptions}
                    getOptionLabel={(option) => option.name || option.id}
                    onChange={(event, newValue, reason) => {
                        if (reason === "selectOption" && newValue) {
                            setSearchValue("");
                            setAuthorizedIdentifiers(prev => [...prev, newValue.id]);
                        }
                    }}
                    value={null}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            onChange={(e) => setSearchValue(e.target.value)}
                            placeholder="Search contact"
                        />
                    )}
                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            {option.name}
                        </li>
                    )}
                    inputValue={searchValue}
                    sx={{ mt: 0.5 }}
                />
                {authorizedIdentifiers.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                        Please select at least one contact.
                    </Typography>
                ) : (
                    authorizedIdentifiers.map((identifier, index) => {
                        const contact: Contact | undefined = contacts.find(contact => contact.id === identifier);
                        return (
                            <Box key={index} display="grid" gap={1} alignItems="center" justifyContent="space-between" gridTemplateColumns="1fr max-content">
                                <Box display="flex" gap={1} alignItems="center">
                                    <Typography>{index + 1}.</Typography>
                                    {contact?.name ? (
                                        <Typography>{contact.name}</Typography>
                                    ) : (
                                        <>
                                            <Typography>{identifier}</Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                (Please add contact to contact book)
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                                <IconButton
                                    size="small"
                                    onClick={() => setAuthorizedIdentifiers(prev => prev.filter(id => id !== identifier))}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        );
                    })
                )}
            </Box>
        </Box>
    );
};